import { PackageIcon } from "lucide-react"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { ClipboardButton } from "@/components/commons/clipboard-button"
import { DataTable } from "@/components/ui/data-table"
import { formatMoney } from "@/helpers/format"

import { createItemsDetailsColumns } from "./items-details-columns"

interface ProductsTableProps {
  items: ItemsDetailsColumnsType[]
}

export const ProductsTable = ({
  items,
}: ProductsTableProps) => {
  const { t } = useTranslation()

  const columns = useMemo(
    () => createItemsDetailsColumns(t),
    [t]
  )

  const totalQuantity = items.reduce(
    (sum, item) => sum + item.quantity,
    0
  )

  const totalAmount = items.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  )

  return (
    <>
      {/* Mobile: Card layout */}
      <div className="space-y-3 md:hidden">
        {items.map((item, index) => (
          <div
            key={item.productId || index}
            className="rounded-lg border bg-card p-4"
          >
            <div className="mb-3 flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-muted">
                <PackageIcon
                  size={18}
                  className="text-muted-foreground"
                />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-medium leading-tight">
                  {item.name}
                </p>
                {item.productId && (
                  <p className="text-xs text-muted-foreground">
                    ID: {item.productId}
                  </p>
                )}
              </div>
            </div>

            {item.sku && (
              <div className="mb-3 flex items-center gap-1">
                <span className="text-xs text-muted-foreground">
                  SKU:
                </span>
                <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
                  {item.sku}
                </code>
                <ClipboardButton
                  text={item.sku}
                  variant="ghost"
                  className="h-5 w-5"
                />
              </div>
            )}

            <div className="flex items-end justify-between border-t pt-3">
              <div className="flex items-center gap-3">
                <div>
                  <p className="text-xs text-muted-foreground">
                    {t("orders.itemsTable.quantity")}
                  </p>
                  <p className="font-medium">
                    {item.quantity}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">
                    {t("orders.itemsTable.unitPrice")}
                  </p>
                  <p className="tabular-nums">
                    {formatMoney(item.price / 100)}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground">
                  {t("orders.itemsTable.subtotal")}
                </p>
                <p className="text-lg font-semibold tabular-nums text-primary">
                  {formatMoney(
                    (item.price * item.quantity) / 100
                  )}
                </p>
              </div>
            </div>
          </div>
        ))}

        {items.length > 0 && (
          <div className="flex items-center justify-between rounded-lg border bg-muted/50 p-4">
            <div className="flex items-center gap-2">
              <span className="font-medium">
                {t("common.total")}
              </span>
              <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                {totalQuantity}{" "}
                {totalQuantity === 1 ? "item" : "itens"}
              </span>
            </div>
            <span className="text-lg font-semibold tabular-nums text-primary">
              {formatMoney(totalAmount / 100)}
            </span>
          </div>
        )}
      </div>

      {/* Desktop: Table layout */}
      <div className="hidden md:block">
        <DataTable
          columns={columns}
          data={items}
          withFooter
        />
      </div>
    </>
  )
}
